/**
 * UsageSliders - Usage pattern controls for wizard
 */

import { Slider } from '@/components/ui/slider';
import type { WizardEnvironment } from '@/stores/wizardStore';

type UsageSlidersProps = {
  environment: WizardEnvironment;
  onChange: (updates: Partial<WizardEnvironment>) => void;
};

const SliderRow = ({
  label,
  description,
  value,
  onValueChange,
  min,
  max,
  step = 1,
  display,
  logarithmic = false,
}: {
  label: string;
  description: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  display: string;
  onValueChange: (value: number) => void;
  logarithmic?: boolean;
}) => {
  const toSliderValue = (actual: number) => {
    if (!logarithmic) return actual;
    return Math.log10(actual);
  };

  const fromSliderValue = (slider: number) => {
    if (!logarithmic) return slider;
    return Math.round(Math.pow(10, slider));
  };

  const sliderValue = toSliderValue(value);
  const sliderMin = toSliderValue(min);
  const sliderMax = toSliderValue(max);

  return (
    <div className="p-4 rounded-lg border bg-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <p className="text-sm font-semibold">{label}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <span className="text-sm font-semibold text-primary">{display}</span>
      </div>
      <Slider
        min={sliderMin}
        max={sliderMax}
        step={logarithmic ? 0.01 : step}
        value={[sliderValue]}
        onValueChange={(vals) => {
          const newValue = fromSliderValue(vals[0] ?? sliderValue);
          onValueChange(newValue);
        }}
        aria-label={label}
      />
    </div>
  );
};

export function UsageSliders({ environment, onChange }: UsageSlidersProps) {
  return (
    <div className="space-y-4">
      {/* Concurrent Users */}
      <SliderRow
        label="Expected Concurrent Users"
        description="Typical number of users actively using the system at the same time"
        value={environment.concurrentUsers}
        min={10}
        max={10000}
        display={`${environment.concurrentUsers.toLocaleString()} concurrent`}
        onValueChange={(val) => onChange({ concurrentUsers: val })}
        logarithmic
      />
    </div>
  );
}
